import React, { useEffect, useState } from "react";
import { Loader2, GitCompare, CheckCircle2, XCircle, X } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * iter-168 — Side-by-side compare for up to 3 passports (spec §6).
 * Enforced server-side; this component surfaces the "Compare up to 3"
 * error cleanly instead of swallowing it.
 */
const CompareDialog = ({ open, onOpenChange, jobId, talentUserIds }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !talentUserIds?.length) { setData(null); return; }
        const run = async () => {
            setLoading(true);
            try {
                const { data: resp } = await api.post(
                    `/employer/jobs/${jobId}/compare`,
                    { talent_user_ids: talentUserIds });
                setData(resp);
            } catch (e) { toast.error(formatApiError(e)); onOpenChange(false); }
            finally { setLoading(false); }
        };
        run();
    }, [open, talentUserIds, jobId, onOpenChange]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="compare-dialog" className="max-w-5xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <GitCompare size={16} className="text-emerald-500" /> Side-by-side compare
                    </DialogTitle>
                    <DialogDescription>
                        Factor-by-factor view of the selected candidates. No scores — just what&apos;s met and what&apos;s missing.
                    </DialogDescription>
                </DialogHeader>
                {loading ? (
                    <div className="py-10 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
                ) : !data?.columns?.length ? (
                    <div className="py-10 text-center text-sm text-muted-foreground" data-testid="compare-empty">
                        Pick 2–3 candidates on the browse page to compare.
                    </div>
                ) : (
                    <div className="overflow-x-auto" data-testid="compare-grid">
                        <table className="w-full text-xs">
                            <thead>
                                <tr>
                                    <th className="text-left p-2 font-semibold text-muted-foreground w-40">Factor</th>
                                    {data.columns.map((c) => (
                                        <th key={c.talent_user_id} data-testid={`compare-col-${c.talent_user_id}`} className="text-left p-2 min-w-[200px]">
                                            <div className="text-sm font-semibold text-foreground">{c.display_name}</div>
                                            <div className="text-[11px] text-muted-foreground">{c.city}{c.city && c.state ? ", " : ""}{c.state}</div>
                                            <div className="mt-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">{c.match_summary}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                <SummaryRow label="Years experience" cols={data.columns} value={(c) => c.years_experience ?? "—"} />
                                <SummaryRow label="Work preference" cols={data.columns} value={(c) => (c.work_type_preference || "—").replace("_", " ")} />
                                <SummaryRow label="Availability" cols={data.columns} value={(c) => (c.availability_status || "—").replace("_", " ")} />
                                <SummaryRow label="All skills" cols={data.columns} value={(c) => (c.skills || []).join(", ") || "—"} />
                                {data.columns[0].factors.map((_, i) => (
                                    <tr key={i}>
                                        <td className="p-2 text-muted-foreground">
                                            {data.columns[0].factors[i].label}
                                            <span className="ml-1 text-[10px] uppercase opacity-60">({data.columns[0].factors[i].kind.replace("_", " ")})</span>
                                        </td>
                                        {data.columns.map((c) => (
                                            <td key={c.talent_user_id} data-testid={`compare-cell-${c.talent_user_id}-${i}`} className="p-2">
                                                {c.factors[i]?.met
                                                    ? <CheckCircle2 size={14} className="text-emerald-500" />
                                                    : <XCircle size={14} className="text-rose-500" />}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

const SummaryRow = ({ label, cols, value }) => (
    <tr>
        <td className="p-2 text-muted-foreground">{label}</td>
        {cols.map((c) => (
            <td key={c.talent_user_id} className="p-2 text-foreground">{value(c)}</td>
        ))}
    </tr>
);

export default CompareDialog;
